import { useState } from 'react';
import { useTranslate } from 'react-polyglot';

import { UserAliasAvatar } from '@genially/ui/build/components/UserAliasAvatar/UserAliasAvatar';

import type { SessionAnalyticsParticipant } from '../../../../utils/buildSessionAnalytics';
import { ColumnScrollArea } from '../common/ColumnScrollArea.styled';
import { UserResultsToggle } from '../common/UserResultsToggle';
import { List, Name, Row, Score } from './ParticipantScores.styled';
import { getScoreTone } from './utils/getScoreTone';

interface ParticipantScoresProps {
  participants: SessionAnalyticsParticipant[];
  /** Resolved autocorrective questions: the same denominator on every row. */
  totalQuestions: number;
}

/**
 * Who in the room is keeping up, which the anonymous side panel deliberately never says.
 * Full screen is the view the host may be projecting, so the names start covered.
 */
export const ParticipantScores = ({
  participants,
  totalQuestions,
}: ParticipantScoresProps) => {
  const t = useTranslate();

  // Deliberately not lifted into the panel's state: this column unmounts when the host
  // collapses back to the side panel, so every entry into full screen starts covered
  // without anything having to reset it.
  const [areNamesVisible, setAreNamesVisible] = useState(false);

  const handleChange = () => {
    setAreNamesVisible(previous => !previous);
  };

  return (
    <>
      {/* The section's own heading doubles as the toggle's label: one row, one name for
          what the column is and for the switch that uncovers it. */}
      <UserResultsToggle
        label={t('__new.play.analyticsResults.participants.title')}
        isChecked={areNamesVisible}
        onChange={handleChange}
      />

      <ColumnScrollArea $isBlurred={!areNamesVisible}>
        <List $isBlurred={!areNamesVisible} data-cy="analytics-participants">
          {participants.map(({ username, correctAnswers }) => (
            <Row key={username}>
              <UserAliasAvatar name={username} size={UserAliasAvatar.Size.Small} />

              <Name>{username}</Name>

              {/* Plain text, not a translated string: it is two numbers and a slash in
                  every language, and this way the tests can assert on it. */}
              <Score $tone={getScoreTone(correctAnswers, totalQuestions)}>
                {`${correctAnswers} / ${totalQuestions}`}
              </Score>
            </Row>
          ))}
        </List>
      </ColumnScrollArea>
    </>
  );
};
